import { Component, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { IFaq, IFaqVisibility } from '@shared/interfaces';
import { FaqService } from '../../../../../../shared/services/faq.service';
import { AdminActionsDirective } from '../../../../directives/admin-actions.directive';
import { AdminService } from '../../../../admin.service';
import { SnackService } from '../../../../../../shared/services/snack.service';
import { DialogService } from '../../../../../../shared/services/dialog.service';
import { ModuleService } from '../../../../../../shared/services/module.service';
import * as uuid from 'uuid';
import { FaqOperations } from '../../operations/admin-faq.operations';
import { IS_PRODUCT_SAFETY_PROJECT, IS_TRS_PROJECT } from '../../../../../../../current-project';

@Component({
    selector: 'app-admin-faq-create',
    templateUrl: './admin-faq-create.component.html',
    styleUrls: ['./admin-faq-create.component.scss'],
    standalone: false
})
export class AdminFaqCreateComponent extends AdminActionsDirective implements OnInit {
    IS_TRS_PROJECT = IS_TRS_PROJECT;
    IS_PRODUCT_SAFETY_PROJECT = IS_PRODUCT_SAFETY_PROJECT;

    visibilities = [IFaqVisibility.All, IFaqVisibility.Admin];
    languages = this.availableLanguages;
    formGroup: UntypedFormGroup;
    currentItem: IFaq;

    deFile: File | null;
    enFile: File | null;

    constructor(
        private formBuilder: UntypedFormBuilder,
        adminService: AdminService,
        dialogService: DialogService,
        moduleService: ModuleService,
        private faqOperations: FaqOperations
    ) {
        super(adminService, dialogService, moduleService);
    }

    get languageGroup(): UntypedFormGroup {
        return this.formBuilder.group(
            this.languages
                .map(e => ({
                    [e]: ['', e === 'en' ? Validators.required : null]
                }))
                .reduce((a, b) => ({ ...a, ...b }), {})
        );
    }

    get faqItem(): IFaq {
        return this.formGroup.value;
    }

    public ngOnInit(): void {
        this.buildForm();
    }

    title(language: string): UntypedFormControl {
        return (this.formGroup?.controls?.title as UntypedFormGroup)?.controls[language] as UntypedFormControl;
    }

    content(language: string): UntypedFormControl {
        return (this.formGroup?.controls?.content as UntypedFormGroup)?.controls[language] as UntypedFormControl;
    }

    onFileSelected(event: any, language: string) {
        switch (language) {
            case 'en':
                this.enFile = event.target.files[0];
                break;

            case 'de':
                this.deFile = event.target.files[0];
                break;

            default:
                throw new Error('Error while selecting file, language not in the specified');
        }
    }

    get isParent(): boolean {
        return !this.formGroup.get('content');
    }

    changeChildren(hasChildren: boolean) {
        if (hasChildren) {
            this.formGroup.removeControl('content');
        } else {
            this.formGroup.addControl('content', this.languageGroup);
            this.formGroup.patchValue({ hasGuidance: false, videoLink: '' });
        }
    }

    private buildForm(): void {
        if (IS_PRODUCT_SAFETY_PROJECT) {
            this.formGroup = this.formBuilder.group({
                parentIds: [this.currentItem?.parentIds ? this.currentItem.parentIds.concat(this.currentItem.id) : []],
                title: this.languageGroup,
                content: this.languageGroup,
                visibility: new FormControl<IFaqVisibility>(null, Validators.required),
                sortIndex: this.currentItem?.items?.length
                    ? this.currentItem.items[this.currentItem.items.length - 1].sortIndex + 1
                    : 1,
                hasGuidance: false,
                videoLink: '',
                // string with the file path in s3
                deFilePath: null,
                enFilePath: null,
                documentsDE: [],
                documentsEN: []
            });
        }

        if (IS_TRS_PROJECT) {
            this.formGroup = this.formBuilder.group({
                parentIds: [this.currentItem?.parentIds ? this.currentItem.parentIds.concat(this.currentItem.id) : []],
                title: this.languageGroup,
                content: this.languageGroup,
                visibility: new FormControl<IFaqVisibility>(null, Validators.required),
                sortIndex: this.currentItem?.items?.length
                    ? this.currentItem.items[this.currentItem.items.length - 1].sortIndex + 1
                    : 1,
                hasGuidance: false,
                videoLink: '',
                documents: []
            });
        }
    }

    async createFaqItem(value: IFaq, faqService: FaqService, snack: SnackService): Promise<void> {
        await faqService.createFaq(value);
        snack.openSnackBarSuccess('admin.faq.snacks.createSuccess');
    }

    async save(event: Event): Promise<void> {
        this.faqOperations.formGroup = this.formGroup;
        this.faqItem.id = uuid.v4().replaceAll('-', '');
        await this.faqOperations.save(event, this.createFaqItem, this.faqItem);
    }
}
